/**
* NotificationCenter - Shows push notification history
* Slides up full-screen matching Settings dialog style
*/
import { useState, useEffect, useCallback } from "react";
import { Bell } from "lucide-react";
import { usePurple } from "../../utils/purple";
import { usePlan } from "../../utils/plan"; // plan_06
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

import { keychainService } from '../../services/keychainService';
const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://worthly-backend-production.up.railway.app';

// plan_06: Money plan - an emoji and a pastel square for each notification.
const PLAN_NOTIF_TONES = ['#FAE4ED', '#DFE9F9', '#ECE6FA', '#DFF2E7', '#FCF3D2'];
const PLAN_NOTIF_WORDS = [
  [/today go|no.?spend|calendar/i, '📅'],
  [/check.?in|week/i, '📊'],
  [/over budget|budget/i, '💸'],
  [/unplanned|spent/i, '🛍️'],
  [/due|payment|bill/i, '⏰'],
  [/money club|post|read/i, '💌'],
  [/goal|saved|savings/i, '🎉'],
  [/remind/i, '🔔'],
  [/log|transaction/i, '✏️'],
];
const planNotifLook = (n, i) => {
  const text = (n.title || '') + ' ' + (n.body || '');
  const hit = PLAN_NOTIF_WORDS.find(([re]) => re.test(n.title || '')) || PLAN_NOTIF_WORDS.find(([re]) => re.test(text));
  return { emoji: hit ? hit[1] : '🔔', bg: PLAN_NOTIF_TONES[i % PLAN_NOTIF_TONES.length] };
};

const NotificationCenter = ({ open, onOpenChange, theme = 'pink' }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const isPink = theme === 'pink';
  const isWarm = theme === 'warm';

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const token = await keychainService.getItem('session_token');
      const res = await fetch(`${API_URL}/api/notifications/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      const token = await keychainService.getItem('session_token');
      await fetch(`${API_URL}/api/notifications/mark-read`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (err) {
      console.error('Failed to mark read:', err);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchNotifications();
    }
  }, [open, fetchNotifications]);

  useEffect(() => {
    if (!open && notifications.length > 0 && notifications.some(n => !n.read)) {
      markAllRead();
    }
  }, [open]);

  const formatTime = (isoStr) => {
    try {
      const date = new Date(isoStr);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
      return '';
    }
  };

  const purple = usePurple();
  const plan = usePlan();
  const themeAccent = purple ? 'text-[#A633C4]' : isPink ? 'text-pink-500' : isWarm ? 'text-[#2D5A3D]' : 'text-indigo-500';
  const themeBgLight = purple ? 'bg-[#F7F0FC]' : isPink ? 'bg-pink-50' : isWarm ? 'bg-[#F0F5F0]' : 'bg-indigo-50';
  const themeBgAccent = purple ? 'bg-[#EFE3F8]' : isPink ? 'bg-pink-100' : isWarm ? 'bg-[#E8F0E8]' : 'bg-indigo-100';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm !fixed !inset-0 !top-0 !left-0 !translate-x-0 !translate-y-0 !max-w-none !w-full !h-full !max-h-full !rounded-none !flex !flex-col !gap-0 overflow-y-auto overflow-x-hidden bg-white" style={{ paddingTop: "env(safe-area-inset-top)", ...(plan ? { background: '#F4F1FB' } : {}) }}>
        {/* plan_06: Money plan - lilac page, one white card of rows */}
        {plan ? (
          <div className="px-5" style={{ paddingTop: 18, paddingBottom: 40 }}>
            <DialogHeader className="text-left">
              <DialogTitle style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-0.9px', color: '#26252E', textAlign: 'left' }}>
                notifications
              </DialogTitle>
            </DialogHeader>
            <p style={{ fontSize: 14.5, color: '#8B879A', marginTop: 6, lineHeight: 1.4 }}>
              Reminders you set yourself are in Your plans on Home
            </p>
            {loading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <div className="w-8 h-8 border-2 border-gray-200 border-t-gray-400 rounded-full animate-spin mb-3" />
                <p style={{ fontSize: 14, color: '#8B879A' }}>Loading notifications...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div style={{ background: '#fff', borderRadius: 28, padding: '28px 20px', marginTop: 18, textAlign: 'center' }}>
                <span style={{ display: 'block', fontSize: 28 }}>{'🔔'}</span>
                <p style={{ fontSize: 15, fontWeight: 600, color: '#26252E', marginTop: 8 }}>No notifications yet</p>
                <p style={{ fontSize: 13.5, color: '#8B879A', marginTop: 4 }}>Push notifications you receive will show up here</p>
              </div>
            ) : (
              <div style={{ background: '#fff', borderRadius: 28, padding: '6px 18px', marginTop: 18 }}>
                {notifications.map((notif, i) => {
                  const look = planNotifLook(notif, i);
                  return (
                    <div key={notif._id || i} style={{ display: 'flex', gap: 12, padding: '14px 0', borderTop: i === 0 ? 'none' : '1px solid #EEEAF6' }}>
                      <span style={{ position: 'relative', width: 40, height: 40, borderRadius: 13, background: look.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
                        {look.emoji}
                        {!notif.read && (
                          <span aria-label="Unread" style={{ position: 'absolute', top: -2, right: -2, width: 10, height: 10, borderRadius: 999, background: '#7A5DD3', border: '2px solid #fff' }} />
                        )}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                          <span style={{ fontWeight: 600, fontSize: 15, color: '#26252E' }}>{notif.title}</span>
                          <span style={{ fontSize: 12, color: '#B5B0C2', whiteSpace: 'nowrap', paddingTop: 2 }}>{formatTime(notif.sent_at)}</span>
                        </div>
                        <p style={{ fontSize: 13.5, lineHeight: 1.5, color: '#6C6879', marginTop: 3 }}>{notif.body}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
        <>
        {/* Header */}
        <DialogHeader className="mt-4">
          <DialogTitle className="text-lg font-normal text-black" style={{ fontFamily: "'DM Serif Display', serif" }}>
            Notification Centre
          </DialogTitle>
        </DialogHeader>

        <div className="px-4 pt-3">
          <p className="text-xs text-gray-400 text-center leading-relaxed">
            Reminders you schedule yourself can be found in Notes, Reminders &amp; Budgets on the home screen.
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 px-4 pt-4 pb-8">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-8 h-8 border-2 border-gray-200 border-t-gray-400 rounded-full animate-spin mb-3" />
              <p className="text-sm text-gray-400">Loading notifications...</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className={`w-16 h-16 rounded-full ${themeBgLight} flex items-center justify-center mb-4`}>
                <Bell className={`w-7 h-7 ${themeAccent} opacity-50`} />
              </div>
              <p className="text-sm font-medium text-gray-500 mb-1">No notifications yet</p>
              <p className="text-xs text-gray-400 text-center px-8">
                Push notifications you receive will show up here
              </p>

            </div>
          ) : (
            <div className="space-y-3">
              {notifications.map((notif, i) => (
                <div
                  key={notif._id || i}
                  className={`rounded-2xl p-4 transition-colors ${
                    !notif.read
                      ? `${themeBgLight} border ${purple ? 'border-[#EDE0F6]' : isPink ? 'border-pink-100' : isWarm ? 'border-[#D4E4D4]' : 'border-indigo-100'}`
                      : 'bg-white border border-gray-100'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <p className={`text-[15px] font-semibold ${!notif.read ? 'text-gray-900' : 'text-gray-700'}`}>
                      {notif.title}
                    </p>
                    <span className="text-[11px] text-gray-400 flex-shrink-0">
                      {formatTime(notif.sent_at)}
                    </span>
                  </div>
                  <p className="text-[13px] text-gray-500 leading-relaxed">
                    {notif.body}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NotificationCenter;
